import { useEffect } from "react";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { isNative } from "@/lib/capacitor";

/**
 * Keep the screen on. Per BRD §3.4: Cooking Mode is "mains sales", a user
 * with floury fingers shouldn't have to wake the device every minute.
 *
 * On native: uses @capacitor-community/keep-awake.
 * On web:    uses the browser Wake Lock API (Chrome/Edge/Opera/Safari TP).
 *
 * Gracefully no-ops on platforms where neither is available.
 */
export function useWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    let webSentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    async function engage() {
      if (isNative()) {
        try {
          await KeepAwake.keepAwake();
        } catch {
          // Plugin not installed in this build — ignore.
        }
        return;
      }
      const wakeLock = (navigator as Navigator & {
        wakeLock?: { request: (t: "screen") => Promise<WakeLockSentinel> };
      }).wakeLock;
      if (!wakeLock) return;
      try {
        webSentinel = await wakeLock.request("screen");
        if (cancelled) {
          await webSentinel.release();
          webSentinel = null;
        }
      } catch {
        // User denied or browser doesn't support.
      }
    }

    void engage();

    return () => {
      cancelled = true;
      if (isNative()) {
        void KeepAwake.allowSleep().catch(() => {});
      } else if (webSentinel) {
        void webSentinel.release().catch(() => {});
        webSentinel = null;
      }
    };
  }, [enabled]);
}

interface WakeLockSentinel {
  release(): Promise<void>;
}
